import { useEffect, useRef, useState } from 'react';

import useSelectPlayerModalAdapter from 'application/components/common/SelectPlayerFlow/SelectPlayerModal/useSelectPlayerModalAdapter';
import { useAccountContext } from 'application/providers/AccountProvider';
import {
  usePointRepositoryInjector,
  useBillingRepositoryInjector,
  useAccountRepositoryInjector,
  usePaymentMethodRepositoryInjector,
} from 'application/providers/DIContainerProvider';
import { useNotificationContext } from 'application/providers/NotificationProvider';
import { TEXTS } from 'application/pages/texts';

import { AcceptanceAdvanceStatus, Country, PaymentKind } from 'domain/api/graphql/generated';
import useAcceptanceAdvanced from 'domain/billing/useAcceptanceAdvanced';

const POLLING_INTERVAL = 10000;
const STOP_POLLING_TIMEOUT = 120000;

const usePointCreatePresenter = () => {
  const stopPullingTimer = useRef<ReturnType<typeof setTimeout>>();
  const [isPolling, setIsPolling] = useState<boolean>(false);

  const { currentCompany } = useAccountContext();
  const notification = useNotificationContext();

  const { userOnboardingFeatures, userOnboardingFeaturesLoading } = useAccountRepositoryInjector();
  const { getPaymentMethod, readPaymentMethod } = usePaymentMethodRepositoryInjector();

  const { paymentMethod } = readPaymentMethod(currentCompany!.id);

  const {
    createPoint,
    createPointLoading,
    createPointError,
    getStandardizedPoints,
    getStandardizedPointsLoading,
    setCurrentPoint,
    watchCurrentPoint,
    readCurrentPoint,
  } = usePointRepositoryInjector();

  const {
    getExtendSubscriptionCost,
    subscriptionCostResult,
    extendOneShotSubscription,
    isExtendSubscriptionLoading,
    getBillingInfo,
    watchBillingInfo,
    licenseCostLoading,
  } = useBillingRepositoryInjector();

  const billingInfo = watchBillingInfo(currentCompany!.id);

  const { hasTrialLicenses, hasActiveLicenses, selectedPaymentKind } = billingInfo;

  const {
    changeStream,
    isChangeStreamLoading,
    copyTemplateStream,
    isCopyStreamTemplateLoading,
    onboardingPassedRequest,
    isUserFeaturesOnboardingPassLoading,
    getSavedStreams,
    isSavedStreamsLoading,
  } = useSelectPlayerModalAdapter();

  const {
    acceptanceAdvanced,
    loading: acceptanceAdvancedLoading,
    startPolling,
    stopPolling,
  } = useAcceptanceAdvanced(currentCompany!.id);

  const isButtonLoading =
    getStandardizedPointsLoading ||
    createPointLoading ||
    isExtendSubscriptionLoading ||
    isChangeStreamLoading ||
    isCopyStreamTemplateLoading ||
    isUserFeaturesOnboardingPassLoading ||
    isSavedStreamsLoading ||
    acceptanceAdvancedLoading;

  const isButtonWithCostLoading =
    isButtonLoading || licenseCostLoading || subscriptionCostResult.data.extendCost === undefined;

  const isAcceptanceAdvancedNull =
    selectedPaymentKind === PaymentKind.Sbbol &&
    (acceptanceAdvanced === null || acceptanceAdvanced?.status === null) &&
    !hasTrialLicenses;

  const isAcceptanceAdvancedPending =
    selectedPaymentKind === PaymentKind.Sbbol && acceptanceAdvanced?.status === AcceptanceAdvanceStatus.Pending;

  const needShowAcceptanceAdvancedModal =
    selectedPaymentKind === PaymentKind.Sbbol && isAcceptanceAdvancedNull && !hasTrialLicenses;

  const startPullingTimeout = () => {
    startPolling(POLLING_INTERVAL);
    setIsPolling(true);

    stopPullingTimer.current = setTimeout(() => {
      stopPolling();
      setIsPolling(false);

      notification.showErrorNotification({
        children: TEXTS.POINT_CREATE_ACCEPTANCE_ADVANCED_TOO_LONG,
        delay: 10000,
      });
    }, STOP_POLLING_TIMEOUT);
  };

  const stopProcess = () => {
    notification.hideNotification();
    stopPolling();
    clearTimeout(stopPullingTimer.current);
    setIsPolling(false);

    notification.showInfoNotification({
      children: TEXTS.POINT_CREATE_ACCEPTANCE_ADVANCED_ACTIVE,
      delay: 10000,
    });
  };

  useEffect(() => {
    switch (acceptanceAdvanced?.status) {
      case AcceptanceAdvanceStatus.Pending: {
        if (!stopPullingTimer.current) {
          startPullingTimeout();

          notification.showInfoNotification({
            children: TEXTS.POINT_CREATE_ACCEPTANCE_ADVANCED_PENDING,
            delay: 10000,
          });
        }

        break;
      }
      case AcceptanceAdvanceStatus.Canceled: {
        notification.showInfoNotification({
          children: TEXTS.POINT_CREATE_ACCEPTANCE_ADVANCED_CANCELLED,
          delay: 10000,
        });

        stopPolling();
        clearTimeout(stopPullingTimer.current);
        setIsPolling(false);
        break;
      }
      case AcceptanceAdvanceStatus.Authorized: {
        if (isPolling) {
          stopProcess();
        }

        break;
      }
      case AcceptanceAdvanceStatus.Active: {
        if (isPolling) {
          stopProcess();
        }

        break;
      }
      case null: {
        stopPolling();
        clearTimeout(stopPullingTimer.current);
        setIsPolling(false);

        break;
      }
      default: {
        break;
      }
    }
  }, [acceptanceAdvanced?.status, isPolling]);

  useEffect(
    () => () => {
      if (stopPullingTimer.current) {
        clearTimeout(stopPullingTimer.current);
      }
    },
    []
  );

  return {
    createPoint,
    getExtendSubscriptionCost,
    extendSubscription: extendOneShotSubscription,
    getStandardizedPoints,
    subscriptionCostResult,
    createPointError,
    userOnboardingFeatures,
    userOnboardingFeaturesLoading,
    isButtonLoading,
    getBillingInfo,
    hasTrialLicenses,
    hasActiveLicenses,
    getPaymentMethod,
    paymentMethod,
    changeStream,
    copyTemplateStream,
    onboardingPassedRequest,
    getSavedStreams,
    setCurrentPoint,
    watchCurrentPoint,
    readCurrentPoint,
    companyCountry: currentCompany?.country || Country.Ru,
    isButtonWithCostLoading,
    needShowAcceptanceAdvancedModal,
    isAcceptanceAdvancedNull:
      isAcceptanceAdvancedNull || (selectedPaymentKind === PaymentKind.Sbbol && acceptanceAdvancedLoading),
    isAcceptanceAdvancedPending,
  };
};

export default usePointCreatePresenter;
